import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const genreApi = createApi({
  reducerPath: 'genreApi',
  baseQuery: fetchBaseQuery({ 
    baseUrl: 'https://api.themoviedb.org/3/',
  }),
  tagTypes: ['GenreMedia'],
  endpoints: (builder) => ({
    getGenreMedia: builder.query({
      query: ({ mediaType, genreId, page = 1, sortBy = 'popularity.desc' }) => ({
        url: `discover/${mediaType}`,
        params: {
          api_key: 'db1a3d4fc9949395b6300a619305310d',
          with_genres: genreId,
          page,
          sort_by: sortBy,
          'release_date.lte': new Date().toISOString().split('T')[0],
        },
      }),
      transformResponse: (response: any) => {
        const results = response.results || [];
        
        // Sort by date first, then popularity if Popular tab is selected
        if (response.sort_by === 'popularity.desc') {
          results.sort((a: any, b: any) => {
            const dateA = new Date(a.release_date || a.first_air_date || 0).getTime();
            const dateB = new Date(b.release_date || b.first_air_date || 0).getTime();

            if (dateB !== dateA) {
              return dateB - dateA; // Newest first
            }
            return (b.popularity || 0) - (a.popularity || 0);
          });
        }

        return {
          items: results,
          totalPages: response.total_pages || 0,
          currentPage: response.page || 1,
          totalResults: response.total_results || 0,
        };
      },
    }),

    getDiscoverMedia: builder.query({
      query: ({
        mediaType,
        page = 1,
        sortBy = "popularity.desc",
        withGenres,
        hideUnreleased,
        voteCountGte,
      }: {
        mediaType: "movie" | "tv";
        page?: number;
        sortBy?: string;
        withGenres?: string;
        hideUnreleased?: boolean;
        voteCountGte?: number;
      }) => {
        const params: Record<string, string | number> = {
          api_key: "db1a3d4fc9949395b6300a619305310d",
          page,
          sort_by: sortBy,
        };
        if (withGenres) {
          params.with_genres = withGenres;
        }
        if (mediaType === "movie" && hideUnreleased) {
          params["release_date.lte"] = new Date().toISOString().split("T")[0];
        }
        if (typeof voteCountGte === "number" && voteCountGte > 0) {
          params["vote_count.gte"] = voteCountGte;
        }
        return {
          url: `discover/${mediaType}`,
          params,
        };
      },
      transformResponse: (response: any) => {
        const results = response.results || [];
        if (response.sort_by === "popularity.desc") {
          results.sort((a: any, b: any) => {
            const dateA = new Date(
              a.release_date || a.first_air_date || 0,
            ).getTime();
            const dateB = new Date(
              b.release_date || b.first_air_date || 0,
            ).getTime();
            if (dateB !== dateA) {
              return dateB - dateA;
            }
            return (b.popularity || 0) - (a.popularity || 0);
          });
        }
        return {
          items: results,
          totalPages: response.total_pages || 0,
          currentPage: response.page || 1,
          totalResults: response.total_results || 0,
        };
      },
    }),

    getRecommendations: builder.query({
      query: ({ mediaType, id }) => ({
        url: `${mediaType}/${id}/recommendations`,
        params: {
          api_key: 'db1a3d4fc9949395b6300a619305310d',
        },
      }),
      transformResponse: (response: any) => {
        const results = response.results || [];
        // Sort by release date (desc), then by popularity (desc)
        results.sort((a: any, b: any) => {
          const dateA = new Date(a.release_date || a.first_air_date || 0).getTime();
          const dateB = new Date(b.release_date || b.first_air_date || 0).getTime();

          if (dateB !== dateA) {
            return dateB - dateA; // Newest first
          }
          return (b.popularity || 0) - (a.popularity || 0);
        });
        return results;
      },
    }),

    // Add Watch Providers API
    getWatchProviders: builder.query({
      query: ({ mediaType, id }) => ({
        url: `${mediaType}/${id}/watch/providers`,
        params: {
          api_key: 'db1a3d4fc9949395b6300a619305310d',
        },
      }),
      transformResponse: (response: any) => {
        // Get US providers (you can change this to other countries)
        const usProviders = response.results?.US || {};
        
        return {
          flatrate: usProviders.flatrate || [], // Streaming services (Netflix, Hulu, etc.)
          rent: usProviders.rent || [], // Rent options (iTunes, Amazon, etc.)
          buy: usProviders.buy || [], // Buy options
          free: usProviders.free || [], // Free streaming (Pluto, Tubi, etc.)
          ads: usProviders.ads || [], // Ad-supported streaming
          theater: usProviders.theater || [], // In theaters
        };
      },
    }),

    getPopularPeople: builder.query({
      query: ({ page = 1 }: { page?: number }) => ({
        url: "person/popular",
        params: {
          api_key: "db1a3d4fc9949395b6300a619305310d",
          page,
        },
      }),
      transformResponse: (response: any) => ({
        results: response.results || [],
        page: response.page || 1,
        totalPages: response.total_pages || 0,
        totalResults: response.total_results || 0,
      }),
    }),

    getPersonDetails: builder.query({
      query: (personId: string) => ({
        url: `person/${personId}`,
        params: {
          api_key: "db1a3d4fc9949395b6300a619305310d",
          append_to_response: "movie_credits,tv_credits",
        },
      }),
    }),
  }),
});

export const {
  useGetGenreMediaQuery,
  useGetDiscoverMediaQuery,
  useGetRecommendationsQuery,
  useGetWatchProvidersQuery,
  useGetPopularPeopleQuery,
  useGetPersonDetailsQuery,
} = genreApi;